import {
  GraphItemTypes,
  IEdge,
  IEnumerable,
  IGraph,
  IInputModeContext,
  IModelItem,
  INode,
  List,
  MoveFocusDirection,
  NavigationInputMode,
  Point as YPoint,
} from '@ardoq/yfiles';
import {
  type Box,
  boxSegments,
  directionsAlongLine,
  findNearestInDirection,
  isAhead,
  isSameDirection,
  leavingDirection,
  type NavigationShape,
  type Point,
  polylineSegments,
} from './navigationGeometry';

const edgePath = (edge: IEdge): Point[] => [
  edge.sourcePort.location,
  ...Array.from(edge.bends, bend => bend.location),
  edge.targetPort.location,
];

const pathLeaving = (edge: IEdge, node: INode): Point[] =>
  edge.sourceNode === node ? edgePath(edge) : edgePath(edge).reverse();

const edgesLeavingInDirection = (
  graph: IGraph,
  node: INode,
  axis: Point
): IEdge[] =>
  Array.from(graph.edgesAt(node)).filter(edge =>
    isSameDirection(leavingDirection(pathLeaving(edge, node)), axis)
  );

const ancestorsOf = (graph: IGraph, node: INode): INode[] => {
  const parent = graph.getParent(node);
  return parent ? [parent, ...ancestorsOf(graph, parent)] : [];
};

/** The group an item sits directly in, or null at the top level. An edge sits in the closest group containing both its ends. */
export const levelOf = (graph: IGraph, item: IModelItem): INode | null => {
  if (item instanceof INode) return graph.getParent(item);
  if (item instanceof IEdge) {
    const targetAncestors = ancestorsOf(graph, item.targetNode);
    return (
      ancestorsOf(graph, item.sourceNode).find(ancestor =>
        targetAncestors.includes(ancestor)
      ) ?? null
    );
  }
  return null;
};

export const isOfItemType = (
  item: IModelItem,
  itemTypes: GraphItemTypes
): boolean =>
  (item instanceof INode && Boolean(itemTypes & GraphItemTypes.NODE)) ||
  (item instanceof IEdge && Boolean(itemTypes & GraphItemTypes.EDGE));

const toAxis = (direction: MoveFocusDirection): Point | null => {
  switch (direction) {
    case MoveFocusDirection.LEFT:
      return { x: -1, y: 0 };
    case MoveFocusDirection.RIGHT:
      return { x: 1, y: 0 };
    case MoveFocusDirection.UP:
      return { x: 0, y: -1 };
    case MoveFocusDirection.DOWN:
      return { x: 0, y: 1 };
    default:
      return null;
  }
};

const toShape = (item: IModelItem): NavigationShape => {
  if (item instanceof INode) {
    return { segments: boxSegments(item.layout), points: [] };
  }
  if (item instanceof IEdge) {
    return {
      segments: polylineSegments(edgePath(item)),
      points: Array.from(item.labels, label => label.layout.center),
    };
  }
  return { segments: [], points: [] };
};

/**
 * Arrow keys follow the graph first: from an edge to the end it points towards, from a node to its
 * own edge in that direction. Otherwise they move to the nearest item in that direction within the
 * current group. yFiles' default searches nodes first, so a node never leads to an edge.
 */
export class AccessibleNavigationInputMode extends NavigationInputMode {
  itemsAtLevel(level: INode | null): IModelItem[] {
    const graph = this.graph;
    if (!graph) return [];
    const scopedToGroups = Boolean(this.navigableItems & GraphItemTypes.NODE);
    return [...graph.nodes, ...graph.edges].filter(
      item =>
        isOfItemType(item, this.navigableItems) &&
        (!scopedToGroups || levelOf(graph, item) === level)
    );
  }

  firstInReadingOrder(items: IModelItem[]): IModelItem | null {
    return (
      items
        .map(item => ({ item, location: this.getReferenceLocation(item) }))
        .sort(
          (a, b) => a.location.y - b.location.y || a.location.x - b.location.x
        )[0]?.item ?? null
    );
  }

  /** Measures to the nearest part of each item — an edge's line, a group's box — instead of a single point per item. */
  protected findNearestItem(
    context: IInputModeContext,
    startItem: IModelItem | null,
    direction: MoveFocusDirection,
    itemsToCheck: IEnumerable<IModelItem>
  ): IModelItem | null {
    const axis = toAxis(direction);
    if (!axis || !startItem) {
      return super.findNearestItem(context, startItem, direction, itemsToCheck);
    }
    return findNearestInDirection(
      this.toOriginBox(startItem),
      axis,
      itemsToCheck
        .toArray()
        .filter(item => item !== startItem)
        .map(item => ({
          item,
          shape: toShape(item),
          referencePoint: this.getReferenceLocation(item),
        }))
    );
  }

  /** An edge is where its label is drawn, which is where the reader sees it. */
  protected getReferenceLocation(item: IModelItem): YPoint {
    const label = item instanceof IEdge ? item.labels.at(0) : null;
    return label ? label.layout.center : super.getReferenceLocation(item);
  }

  /**
   * Follows the line as drawn: from an edge, the end its line heads towards from the label;
   * from a node, its own edges that leave it in that direction. The nearest one ahead wins.
   */
  private connectedInDirection(
    item: IModelItem,
    axis: Point
  ): IModelItem | null {
    const origin = this.getReferenceLocation(item);
    return (
      this.connectedCandidates(item, axis)
        .filter(candidate => isOfItemType(candidate, this.navigableItems))
        .map(candidate => ({
          candidate,
          location: this.getReferenceLocation(candidate),
        }))
        .filter(({ location }) => isAhead(origin, location, axis))
        .map(({ candidate, location }) => ({
          candidate,
          distance: location.distanceTo(origin),
        }))
        .sort((a, b) => a.distance - b.distance)[0]?.candidate ?? null
    );
  }

  private connectedCandidates(item: IModelItem, axis: Point): IModelItem[] {
    if (item instanceof IEdge) return this.endsInDirection(item, axis);
    const graph = this.graph;
    if (!graph || !(item instanceof INode)) return [];
    return edgesLeavingInDirection(graph, item, axis);
  }

  private endsInDirection(edge: IEdge, axis: Point): INode[] {
    const { towardStart, towardEnd } = directionsAlongLine(
      edgePath(edge),
      this.getReferenceLocation(edge)
    );
    return [
      ...(isSameDirection(towardStart, axis) ? [edge.sourceNode] : []),
      ...(isSameDirection(towardEnd, axis) ? [edge.targetNode] : []),
    ];
  }

  private toOriginBox(item: IModelItem): Box {
    if (item instanceof INode) {
      const { x, y, width, height } = item.layout;
      return { x, y, width, height };
    }
    const { x, y } = this.getReferenceLocation(item);
    return { x, y, width: 0, height: 0 };
  }

  protected findNextItem(
    context: IInputModeContext,
    direction: MoveFocusDirection
  ): IModelItem | null {
    const graph = this.graph;
    const current = this.graphComponent?.currentItem ?? null;
    if (
      graph &&
      direction === MoveFocusDirection.PARENT &&
      current instanceof IEdge
    ) {
      const group = levelOf(graph, current);
      return group && isOfItemType(group, this.navigableItems) ? group : null;
    }
    const axis = toAxis(direction);
    if (!graph || !axis) {
      return super.findNextItem(context, direction);
    }
    if (!current) return this.firstInReadingOrder(this.itemsAtLevel(null));
    const connected = this.connectedInDirection(current, axis);
    if (connected) return connected;
    return this.findNearestItem(
      context,
      current,
      direction,
      new List(this.itemsAtLevel(levelOf(graph, current)))
    );
  }
}
